// src/modules/menu/MenuManager.jsx
// Administrador del menú: secciones plegables con sus opciones, búsqueda, ocultar/mostrar sin borrar,
// vista previa "ver el menú como un rol" y un formulario guiado para crear o editar.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu,
  MenuItem, Paper, Snackbar, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Clear as ClearIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from "@mui/icons-material";

import menuService from "./menuService";
import DashboardLayout from "../../layouts/DashboardLayout";
import { listRoles } from "../../services/rolesService";
import RUTAS_CONOCIDAS from "./rutasConocidas";

/* ===================== Utilidades del árbol ===================== */

const normalizar = (nodes) =>
  Array.isArray(nodes)
    ? nodes.map((n) => ({
        ...n,
        to: n.to ?? n.path ?? "",
        roles: Array.isArray(n.roles) ? n.roles : [],
        active: n.active === 0 ? 0 : 1,
        children: normalizar(n.children),
      }))
    : [];

// Lista plana con la profundidad y la ruta "Sección › Opción".
const aplanar = (nodes, depth = 0, ancestros = [], out = []) => {
  nodes.forEach((n) => {
    const ruta = [...ancestros, n.label];
    out.push({ node: n, depth, ruta: ruta.join(" › ") });
    aplanar(n.children, depth + 1, ruta, out);
  });
  return out;
};

const contarDescendientes = (node) => aplanar(node.children).length;

// Cambios inmediatos en el árbol en memoria: la pantalla responde al instante y el servidor se actualiza detrás.
const cambiarNodo = (nodes, id, fn) => nodes.map((n) => (n.id === id ? fn(n) : { ...n, children: cambiarNodo(n.children, id, fn) }));
const quitarNodo = (nodes, id) => nodes.filter((n) => n.id !== id).map((n) => ({ ...n, children: quitarNodo(n.children, id) }));
const intercambiarNodo = (nodes, id, direccion) => {
  const i = nodes.findIndex((n) => n.id === id);
  if (i >= 0) {
    const j = direccion === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= nodes.length) return nodes;
    const copia = [...nodes];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    return copia;
  }
  return nodes.map((n) => ({ ...n, children: intercambiarNodo(n.children, id, direccion) }));
};

const puedeVer = (rol, node) => rol === "admin" || (node.roles || []).includes(rol);

// Lo que ve un rol: opciones visibles que su rol permite (o que contienen alguna que sí); las ocultas desaparecen con sus hijas.
const filtrarPorRol = (nodes, rol) =>
  nodes.flatMap((n) => {
    if (n.active === 0) return [];
    const hijos = filtrarPorRol(n.children, rol);
    if (!puedeVer(rol, n) && hijos.length === 0) return [];
    return [{ ...n, children: hijos }];
  });

// Búsqueda: si una opción coincide se muestra completa; si no, solo las ramas donde coincide alguna hija.
const filtrarPorTexto = (nodes, texto) =>
  nodes.flatMap((n) => {
    const coincide =
      n.label.toLowerCase().includes(texto) ||
      (n.to || "").toLowerCase().includes(texto) ||
      n.roles.some((r) => r.toLowerCase().includes(texto));
    if (coincide) return [n];
    const hijos = filtrarPorTexto(n.children, texto);
    return hijos.length ? [{ ...n, children: hijos }] : [];
  });

/* ===================== Piezas pequeñas ===================== */

function RolesResumen({ roles, etiqueta }) {
  if (!roles.length) return <Chip size="small" variant="outlined" color="warning" label="Solo admin" />;
  const visibles = roles.slice(0, 3);
  const resto = roles.slice(3);
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {visibles.map((r) => <Chip key={r} size="small" label={etiqueta(r)} />)}
      {resto.length > 0 && (
        <Tooltip title={resto.map(etiqueta).join(", ")}>
          <Chip size="small" variant="outlined" label={`+${resto.length}`} />
        </Tooltip>
      )}
    </Stack>
  );
}

const FORM_VACIO = { id: null, label: "", path: "", roles: [], parent_id: null, active: true, aplicarAHijas: false };

/* ===================== Pantalla ===================== */

export default function MenuManager({ embedded = false }) {
  const [cargando, setCargando] = useState(true);
  const [tree, setTree] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null); // { texto, tipo }

  const [busqueda, setBusqueda] = useState("");
  const [verComo, setVerComo] = useState("");
  const [expandidas, setExpandidas] = useState(() => new Set());

  const [menuAcciones, setMenuAcciones] = useState(null); // { el, node }
  const [dialogAbierto, setDialogAbierto] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [errorForm, setErrorForm] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    listRoles().then(({ data }) => setAllRoles(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const etiquetaRol = useCallback((nombre) => allRoles.find((r) => r.nombre === nombre)?.label || nombre, [allRoles]);

  // silencioso: vuelve a pedir el menú sin el indicador de carga (la lista no parpadea ni salta).
  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setCargando(true);
    setError(null);
    try {
      const resp = await menuService.exportTree();
      const data = resp?.data?.sections ? resp.data : resp;
      setTree(normalizar(data?.sections));
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 403 ? "Solo un administrador puede editar el menú." : e?.response?.data?.error || e?.message || "Error cargando el menú");
    } finally {
      if (!silencioso) setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const avisar = (texto, tipo = "success") => setAviso({ texto, tipo });

  /* ---------- Datos derivados ---------- */

  const todas = useMemo(() => aplanar(tree), [tree]);
  const totalOpciones = todas.filter((x) => x.depth > 0).length;
  const totalOcultas = todas.filter((x) => x.node.active === 0).length;

  const sugerenciasRuta = useMemo(
    () => [...new Set([...RUTAS_CONOCIDAS, ...todas.map((x) => x.node.to).filter(Boolean)])].sort(),
    [todas]
  );

  const soloLectura = Boolean(verComo);
  const textoBusqueda = busqueda.trim().toLowerCase();
  const filtrando = soloLectura || Boolean(textoBusqueda);

  const arbolMostrado = useMemo(() => {
    let nodos = tree;
    if (verComo) nodos = filtrarPorRol(nodos, verComo);
    if (textoBusqueda) nodos = filtrarPorTexto(nodos, textoBusqueda);
    return nodos;
  }, [tree, verComo, textoBusqueda]);

  /* ---------- Acciones ---------- */

  const mensajeError = (e) => e?.response?.data?.error || e?.message || "No se pudo completar la acción";

  // Para acciones que crean datos nuevos: se guarda y se recarga en silencio para traer los ids nuevos.
  const ejecutar = async (fn, mensajeOk) => {
    try {
      await fn();
      await cargar({ silencioso: true });
      if (mensajeOk) avisar(mensajeOk);
    } catch (e) {
      avisar(mensajeError(e), "error");
    }
  };

  // Para acciones que solo cambian algo existente: se ve al instante; si falla, se deshace recargando.
  const optimista = async (aplicarLocal, llamada, mensajeOk) => {
    setTree(aplicarLocal);
    try {
      await llamada();
      if (mensajeOk) avisar(mensajeOk);
    } catch (e) {
      avisar(mensajeError(e), "error");
      await cargar({ silencioso: true });
    }
  };

  // parent_id se envía siempre para que ninguna actualización mueva la opción de lugar.
  const alternarVisible = (node) => {
    const nuevo = node.active === 0 ? 1 : 0;
    return optimista(
      (t) => cambiarNodo(t, node.id, (n) => ({ ...n, active: nuevo })),
      () => menuService.updateItem(node.id, { active: nuevo === 1, parent_id: node.parent_id ?? null }),
      nuevo === 1 ? "Opción visible de nuevo." : "Opción oculta (no se borró)."
    );
  };

  const mover = (node, direccion) =>
    optimista((t) => intercambiarNodo(t, node.id, direccion), () => menuService.reorderItem(node.id, direccion));

  const duplicar = (node) =>
    ejecutar(
      () => menuService.createItem({ label: `${node.label} (copia)`, path: node.to, parent_id: node.parent_id ?? null, roles: node.roles }),
      "Opción duplicada al final de su sección."
    );

  const eliminar = (node) => {
    const n = contarDescendientes(node);
    const texto = n > 0 ? `¿Eliminar "${node.label}" y sus ${n} opciones de dentro? No se puede deshacer.\n\nSi solo quieres quitarla del menú, ocúltala en vez de eliminarla.`
      : `¿Eliminar "${node.label}"? No se puede deshacer.\n\nSi solo quieres quitarla del menú, ocúltala en vez de eliminarla.`;
    if (!window.confirm(texto)) return;
    optimista((t) => quitarNodo(t, node.id), () => menuService.deleteItem(node.id), "Opción eliminada.");
  };

  /* ---------- Formulario ---------- */

  const abrirNueva = (padre = null) => {
    setForm({ ...FORM_VACIO, parent_id: padre?.id ?? null });
    setErrorForm(null);
    setDialogAbierto(true);
  };

  const abrirEditar = (node) => {
    setForm({
      id: node.id, label: node.label, path: node.to, roles: node.roles, parent_id: node.parent_id ?? null,
      active: node.active !== 0, aplicarAHijas: false,
    });
    setErrorForm(null);
    setDialogAbierto(true);
  };

  const cerrarDialog = () => { if (!guardando) setDialogAbierto(false); };

  const nodoEditado = form.id ? todas.find((x) => x.node.id === form.id)?.node : null;
  const hijasEditadas = nodoEditado ? aplanar(nodoEditado.children).map((x) => x.node) : [];

  // No se puede elegir como padre a la propia opción ni a una de sus hijas.
  const opcionesPadre = useMemo(() => {
    const excluidos = new Set();
    if (nodoEditado) {
      excluidos.add(nodoEditado.id);
      aplanar(nodoEditado.children).forEach((x) => excluidos.add(x.node.id));
    }
    return todas.filter((x) => !excluidos.has(x.node.id));
  }, [todas, nodoEditado]);

  const rutaPadre = form.parent_id ? todas.find((x) => x.node.id === form.parent_id)?.ruta : null;

  const alternarRol = (rol) =>
    setForm((f) => ({ ...f, roles: f.roles.includes(rol) ? f.roles.filter((r) => r !== rol) : [...f.roles, rol] }));

  const guardar = async () => {
    const label = form.label.trim();
    const path = form.path.trim();
    if (!label || !path) { setErrorForm("El nombre y la ruta son obligatorios."); return; }
    setErrorForm(null);
    setGuardando(true);
    try {
      const payload = { label, path, roles: form.roles, parent_id: form.parent_id || null };
      if (form.id) {
        await menuService.updateItem(form.id, { ...payload, active: form.active });
        if (form.aplicarAHijas) {
          for (const h of hijasEditadas) await menuService.updateItem(h.id, { roles: form.roles, parent_id: h.parent_id ?? null });
        }
      } else {
        await menuService.createItem(payload);
      }
      setDialogAbierto(false);
      await cargar({ silencioso: true });
      avisar(form.id ? "Cambios guardados." : "Opción creada al final de su sección.");
    } catch (e) {
      setErrorForm(e?.response?.data?.error || e?.message || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  /* ---------- Render ---------- */

  const alternarSeccion = (id) =>
    setExpandidas((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });

  const accionesNodo = (node, idx, total) => (
    <Stack direction="row" spacing={0.25} alignItems="center" onClick={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()}>
      <Tooltip title="Subir"><span><IconButton size="small" disabled={filtrando || idx === 0} onClick={() => mover(node, "up")}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
      <Tooltip title="Bajar"><span><IconButton size="small" disabled={filtrando || idx === total - 1} onClick={() => mover(node, "down")}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
      <Tooltip title={node.active === 0 ? "Oculta: actívala para que se vea en el menú" : "Visible: apágala para ocultarla sin borrarla"}>
        <Switch size="small" checked={node.active !== 0} onChange={() => alternarVisible(node)} />
      </Tooltip>
      <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(node)}><EditIcon fontSize="inherit" /></IconButton></Tooltip>
      <Tooltip title="Más acciones"><IconButton size="small" onClick={(e) => setMenuAcciones({ el: e.currentTarget, node })}><MoreVertIcon fontSize="inherit" /></IconButton></Tooltip>
    </Stack>
  );

  const renderFila = (node, depth, idx, total) => (
    <React.Fragment key={node.id}>
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", py: 0.6, pr: 1, pl: 1.5 + depth * 3,
          borderBottom: 1, borderColor: "divider", opacity: node.active === 0 ? 0.55 : 1, "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" fontWeight={600} noWrap>{node.label}</Typography>
            {node.active === 0 && <Chip size="small" color="default" label="Oculta" />}
            {node.children.length > 0 && <Chip size="small" variant="outlined" label={`${node.children.length} dentro`} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: "monospace", display: "block" }}>{node.to}</Typography>
        </Box>
        <Box sx={{ flex: "1 1 200px" }}><RolesResumen roles={node.roles} etiqueta={etiquetaRol} /></Box>
        {!soloLectura && accionesNodo(node, idx, total)}
      </Box>
      {node.children.map((h, i) => renderFila(h, depth + 1, i, node.children.length))}
    </React.Fragment>
  );

  const renderSeccion = (sec, idx, total) => {
    const abierta = filtrando || expandidas.has(sec.id);
    const dentro = contarDescendientes(sec);
    return (
      <Accordion
        key={sec.id} expanded={abierta} onChange={() => !filtrando && alternarSeccion(sec.id)} disableGutters variant="outlined"
        sx={{ "&:before": { display: "none" }, opacity: sec.active === 0 ? 0.6 : 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", pr: 1, flexWrap: "wrap" }} useFlexGap>
            <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography fontWeight={800}>{sec.label}</Typography>
                <Chip size="small" variant="outlined" label={`${dentro} ${dentro === 1 ? "opción" : "opciones"}`} />
                {sec.active === 0 && <Chip size="small" label="Oculta" />}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{sec.to}</Typography>
            </Box>
            <Box sx={{ flex: "1 1 160px" }}><RolesResumen roles={sec.roles} etiqueta={etiquetaRol} /></Box>
            {!soloLectura && accionesNodo(sec, idx, total)}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {sec.children.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Esta sección no tiene opciones todavía.</Typography>
          ) : (
            sec.children.map((h, i) => renderFila(h, 0, i, sec.children.length))
          )}
          {!soloLectura && (
            <Box sx={{ p: 1 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => abrirNueva(sec)}>Agregar opción en "{sec.label}"</Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const contenido = (
    <Box sx={{ p: embedded ? 0 : 1.5 }}>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
          <TextField
            size="small" placeholder="Buscar opción, ruta o rol…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            sx={{ minWidth: { md: 280 } }}
            slotProps={{ input: {
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: busqueda ? <InputAdornment position="end"><IconButton size="small" onClick={() => setBusqueda("")}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : null,
            } }}
          />
          <TextField select size="small" label="Ver el menú como…" value={verComo} onChange={(e) => setVerComo(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">(Editar el menú)</MenuItem>
            {allRoles.map((r) => <MenuItem key={r.nombre} value={r.nombre}>{r.label}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<UnfoldMoreIcon />} disabled={filtrando} onClick={() => setExpandidas(new Set(tree.map((s) => s.id)))}>Expandir todo</Button>
          <Button size="small" startIcon={<UnfoldLessIcon />} disabled={filtrando} onClick={() => setExpandidas(new Set())}>Contraer todo</Button>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => cargar()}>Actualizar</Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={soloLectura} onClick={() => abrirNueva(null)}>Nueva sección</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          {tree.length} secciones · {totalOpciones} opciones{totalOcultas > 0 ? ` · ${totalOcultas} ocultas` : ""}
        </Typography>
      </Paper>

      {soloLectura && (
        <Alert severity="info" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => setVerComo("")}>Volver a editar</Button>}>
          Así ve el menú el rol <strong>{etiquetaRol(verComo)}</strong>. Es solo una vista previa: no se puede editar desde aquí.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {cargando ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /><Typography variant="body2" sx={{ mt: 1.5 }}>Cargando menú…</Typography></Stack>
      ) : arbolMostrado.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {filtrando ? "Nada coincide con lo que buscas." : "El menú está vacío. Crea la primera sección."}
        </Typography>
      ) : (
        <Stack spacing={1}>{arbolMostrado.map((s, i) => renderSeccion(s, i, arbolMostrado.length))}</Stack>
      )}

      {/* Más acciones de una opción */}
      <Menu anchorEl={menuAcciones?.el} open={Boolean(menuAcciones)} onClose={() => setMenuAcciones(null)}>
        <MenuItem onClick={() => { const n = menuAcciones.node; setMenuAcciones(null); abrirNueva(n); }}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon><ListItemText>Agregar una opción dentro</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const n = menuAcciones.node; setMenuAcciones(null); duplicar(n); }}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon><ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <MenuItem sx={{ color: "error.main" }} onClick={() => { const n = menuAcciones.node; setMenuAcciones(null); eliminar(n); }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Eliminar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Crear / editar */}
      <Dialog open={dialogAbierto} onClose={cerrarDialog} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? "Editar opción" : form.parent_id ? "Nueva opción" : "Nueva sección"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {errorForm && <Alert severity="error">{errorForm}</Alert>}
            <TextField label="Nombre que verá el usuario" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} autoFocus required />
            <Autocomplete
              freeSolo options={sugerenciasRuta} inputValue={form.path}
              onInputChange={(_, v) => setForm((f) => ({ ...f, path: v }))}
              renderInput={(p) => <TextField {...p} label="Ruta de la pantalla" required helperText="Elige una de la lista o escribe una (ej.: /factura-manual). Para una sección que solo agrupa, usa su ruta principal." />}
            />
            <TextField
              select label="Dentro de" value={form.parent_id ?? ""} onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value === "" ? null : e.target.value }))}
              helperText={form.parent_id ? "La opción aparecerá dentro de esa sección." : "Sin selección: es una sección principal."}
            >
              <MenuItem value="">(Sección principal)</MenuItem>
              {opcionesPadre.map((o) => <MenuItem key={o.node.id} value={o.node.id}>{`${"— ".repeat(o.depth)}${o.node.label}`}</MenuItem>)}
            </TextField>

            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">¿Qué roles la ven?</Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" onClick={() => setForm((f) => ({ ...f, roles: allRoles.map((r) => r.nombre) }))}>Todos</Button>
                  <Button size="small" onClick={() => setForm((f) => ({ ...f, roles: [] }))}>Ninguno</Button>
                </Stack>
              </Stack>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75}>
                {allRoles.map((r) => {
                  const sel = form.roles.includes(r.nombre);
                  return <Chip key={r.nombre} label={r.label} color={sel ? "primary" : "default"} variant={sel ? "filled" : "outlined"} onClick={() => alternarRol(r.nombre)} />;
                })}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {form.roles.length === 0 ? "Sin roles marcados: solo el administrador la verá." : "El administrador siempre ve todo el menú."}
              </Typography>
            </Box>

            {form.id && hijasEditadas.length > 0 && (
              <FormControlLabel
                control={<Checkbox checked={form.aplicarAHijas} onChange={(e) => setForm((f) => ({ ...f, aplicarAHijas: e.target.checked }))} />}
                label={`Aplicar estos mismos roles a las ${hijasEditadas.length} opciones de dentro`}
              />
            )}
            {form.id && (
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
                label={form.active ? "Visible en el menú" : "Oculta (no se muestra a nadie)"}
              />
            )}

            <Alert severity="info" icon={false} sx={{ py: 0 }}>
              Se verá en: <strong>{rutaPadre ? `${rutaPadre} › ` : ""}{form.label.trim() || "…"}</strong>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialog} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>{guardando ? "Guardando…" : "Guardar"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(aviso)} autoHideDuration={3500} onClose={() => setAviso(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {aviso ? <Alert severity={aviso.tipo} onClose={() => setAviso(null)} variant="filled">{aviso.texto}</Alert> : undefined}
      </Snackbar>
    </Box>
  );

  if (embedded) return contenido;
  return <DashboardLayout>{contenido}</DashboardLayout>;
}
